import React, { useCallback, useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from 'src/components/Table/TableContainer';
import { columnsOrderDetails, detailsHeadings } from './constants';
import TableHeading from 'src/components/Table/TableHeading';
import { Box, TableBody } from '@mui/material';
import NoDataCell from 'src/components/NoDataCell';
import { pagesText } from 'src/constants/pagesText';
import Row from 'src/components/Table/Row';
import LoadingRows from 'src/components/Table/LoadingRows';
import { useParams } from 'react-router-dom';
import useQueryParams from 'src/hooks/useQueryParams';
import useDebounceHandler from 'src/hooks/useDebounceHandler';
import { defaultSearchParams, reversedSortMap, ROW_SOURCES, sortingMethods } from 'src/constants/table';
import TableSearch from 'src/components/elements/TableSearch';
import useTableSort from '../../../../../../hooks/useTableSort';
import { defaultScriptExecutionDevicesFilters } from '../../../../../../components/Filters/constants';
import FilterCount from '../../../../../../components/Filters/FilterCount';
import useGetScriptExecutionAffectedDevices
  from '../../../../../../queries/scriptExecutionDetails/useGetScriptExecutionAffectedDevices';
import { checkHasFilters } from '../../../../../../utils/table.util';
import GenericTableCell from '../../../../../../components/GenericTableCell';
import { isDateString } from '../../../../../../utils/date.util';
import { formatDate } from '../../../../../../utils/formatTime';
import WdacScriptExecutionFiltersSidebar from './WdacScriptExecutionFiltersSidebar';


const ScriptExecutionDevices = () => {
  const [isOpenFilters, setIsOpenFilters] = useState(false);
  const { scriptExecutionId } = useParams();

  const { isLoading, data, lastElementRef, isFetching, count, query } = useGetScriptExecutionAffectedDevices({
    scriptExecutionId,
  });

  const {
    queryObject: { page, rowsPerPage, filters = {}, searchValue: initialSearchValue },
    onApplySearchParams,
    changeFieldInURL,
  } = useQueryParams();

  const [searchValue, setSearchValue] = useState(initialSearchValue);

  const renderCustomCell = useCallback((column, row) => {

    return (
      <GenericTableCell
        key={column?.name || column}
        isNumber={column?.isNumber}
        value={

             isDateString(row[column?.name || column])
              ? formatDate(row[column?.name || column])
              : row[column?.name || column]
        }        styles={column?.styles || {}}
        {...column?.props}
      />
    );
  }, []);

  const onCloseFilters = useCallback(() => {
    setIsOpenFilters(false);
  }, []);

  const { onSort, onSortTable, onResetSortField, getListItemSortMethod, getCellSortMethod } =
    useTableSort({
      filters,
      onApplySearchParams,
      page,
      rowsPerPage,
      searchValue,
      defaultFilters: defaultScriptExecutionDevicesFilters,
    });

  const debounceQueryHandler = useDebounceHandler((key, value) => {
    changeFieldInURL([{ key, value }]);
  }, 500);

  const onChangeSearchValue = useCallback(
    (event) => {
      setSearchValue(event.target.value);
      debounceQueryHandler('search', event.target.value.trim());
    },
    [debounceQueryHandler]
  );

  const onCellSort = useCallback(
    ({ field, sortMethod, sortingCellField, sortFieldName }) => {
      const reversedSortOrder =
        sortMethod === sortingMethods.initial ? sortingMethods.desc : reversedSortMap[sortMethod];
      onSort(field, reversedSortOrder, sortingCellField, sortFieldName);
    },
    [onSort]
  );

  const onResetSearch = useCallback(() => {
    setSearchValue('');
    changeFieldInURL([defaultSearchParams]);
  }, [changeFieldInURL]);

  const hasFilters = useMemo(() => checkHasFilters(filters), [filters]);

  const filteredCount = useMemo(() => {
    return !isLoading && (hasFilters || searchValue) ? count : null;
  }, [count, hasFilters, isLoading, searchValue]);

  const onOpenFilters = useCallback(() => {
    setIsOpenFilters(true);
  }, []);

  const onApplyFilters = useCallback(
    (filters) => {
      onApplySearchParams({ filters, searchValue });
    },
    [onApplySearchParams, searchValue]
  );

  return (
    <Box sx={{ position: 'relative' }} mt={4}>
      <FilterCount filteredCount={filteredCount} onOpenFilters={onOpenFilters} />
      <TableSearch
        searchValue={searchValue}
        onChangeSearchValue={onChangeSearchValue}
        onResetSearch={onResetSearch}
      />
      <Paper elevation={0} sx={{ display: 'flex', flexDirection: 'column' }}>
        <TableContainer square component={Paper} elevation={0} styles={{ maxHeight: `calc(100vh - 368px)` }}>
          <Table stickyHeader>
            <TableHeading
              headings={detailsHeadings}
              onSort={onSort}
              filterTopPisition="0"
              onSortTable={onSortTable}
              onResetSortField={onResetSortField}
              getListItemSortMethod={getListItemSortMethod}
              getCellSortMethod={getCellSortMethod}
              onCellSort={onCellSort}
              listCellStyles={{ top: '0' }}
            />
            <TableBody>
              {!isLoading && !isFetching && !data?.length && (
                <NoDataCell colSpan={6}>{pagesText.noDataFound}</NoDataCell>
              )}
              {!!data?.length &&
                data.map((row, index) => (
                  <Row
                    withTwoColors={true}
                    ref={index === data.length - 1 ? lastElementRef : null}
                    key={index}
                    row={row}
                    columnData={columnsOrderDetails}
                    hasActions={false}
                    highlightText={true}
                    source={ROW_SOURCES.devices}
                    isClickable={true}
                    renderCustomCell={renderCustomCell}
                  />
                ))}
            </TableBody>
            {(isLoading || isFetching) && <LoadingRows columnCount={5} rowCount={5} />}
          </Table>
        </TableContainer>
        <WdacScriptExecutionFiltersSidebar
          query={query}
          open={isOpenFilters}
          onClose={onCloseFilters}
          onApplyFilters={onApplyFilters}
          initialQueryFilters={filters}
        />
      </Paper>
    </Box>
  );
};

export default ScriptExecutionDevices;
